import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import {
  Radio, Mic2, Play, Share2, Check, ArrowRight, Calendar as CalIcon,
  Headphones, ExternalLink, AlertTriangle
} from "lucide-react";
import { api } from "@/lib/api";

const PLATFORM_ICONS = {
  facebook: "Facebook",
  instagram: "Instagram",
  twitter: "X",
  tiktok: "TikTok",
  youtube: "YouTube",
  linkedin: "LinkedIn",
  whatsapp: "WhatsApp",
};

export default function PublicShowPage() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    api.get(`/public/shows/${slug}`)
      .then((r) => {
        setData(r.data);
        // Update SEO meta
        document.title = `${r.data.show.name} — ${r.data.station.name}`;
        const desc = r.data.show.description || r.data.station.tagline || `Listen to ${r.data.show.name} on ${r.data.station.name}.`;
        let m = document.querySelector('meta[name="description"]');
        if (!m) { m = document.createElement("meta"); m.name = "description"; document.head.appendChild(m); }
        m.content = desc.slice(0, 160);
      })
      .catch(() => setErr(true));
  }, [slug]);

  const share = async () => {
    const url = window.location.href;
    if (navigator.share) {
      try { await navigator.share({ title: data?.show?.name, url }); return; } catch {}
    }
    navigator.clipboard.writeText(url);
    setCopied(true);
    setTimeout(() => setCopied(false), 1800);
  };

  if (err) {
    return (
      <div className="min-h-screen bg-bg text-ink-primary flex items-center justify-center p-6">
        <div className="text-center">
          <AlertTriangle className="h-10 w-10 mx-auto text-brand-onair mb-3" />
          <div className="font-display text-2xl font-bold mb-2">Show not found</div>
          <p className="text-sm text-ink-secondary mb-6">This show might be private or no longer available.</p>
          <Link to="/" className="rounded-md bg-brand-amber px-5 py-2 font-semibold text-black amber-glow">Back to BroadcastFlow AI</Link>
        </div>
      </div>
    );
  }

  if (!data) {
    return <div className="min-h-screen bg-bg text-ink-muted font-mono text-xs flex items-center justify-center">LOADING SHOW...</div>;
  }

  const { show, station, dj, episodes } = data;
  const liveUrl = station.shoutcast_url || station.icecast_url;
  const socials = Object.entries(PLATFORM_ICONS)
    .map(([k, label]) => ({ key: k, label, url: station[k] }))
    .filter((s) => s.url);

  return (
    <div className="min-h-screen bg-bg text-ink-primary" data-testid="public-show-page">
      {/* Header */}
      <header className="sticky top-0 z-30 border-b border-line bg-bg/70 backdrop-blur-xl">
        <div className="mx-auto max-w-5xl flex items-center justify-between px-6 h-14">
          <Link to="/" className="flex items-center gap-3" data-testid="public-logo">
            <img src="/brand/broadcastflowai-hero.png" alt="BroadcastFlow AI" className="h-10 w-auto object-contain" />
            <span className="text-xs text-ink-muted ml-2">/ {station.name}</span>
          </Link>
          <button
            onClick={share}
            data-testid="share-show-btn"
            className="inline-flex items-center gap-2 rounded-md border border-line bg-bg-elevated px-3 py-1.5 text-xs font-medium hover:bg-bg-surface transition"
          >
            {copied ? <Check className="h-3.5 w-3.5 text-brand-amber" /> : <Share2 className="h-3.5 w-3.5" />}
            {copied ? "Copied" : "Share"}
          </button>
        </div>
      </header>

      {/* Hero */}
      <section className="relative border-b border-line overflow-hidden">
        <div className="absolute inset-0 scan-line opacity-50" />
        <div className="relative mx-auto max-w-5xl px-6 py-16">
          <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-brand-amber mb-3 flex items-center gap-2">
            <span className="h-1.5 w-1.5 rounded-full bg-brand-onair onair-dot" />
            // SHOW · {show.genre || station.genre || "Radio"}
          </div>
          <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl font-black tracking-tighter leading-[0.95]" data-testid="show-title">
            {show.name}
          </h1>
          {show.description && (
            <p className="mt-6 max-w-2xl text-base sm:text-lg text-ink-secondary leading-relaxed">
              {show.description}
            </p>
          )}
          <div className="mt-8 flex flex-wrap items-center gap-4">
            {liveUrl && (
              <a
                href={liveUrl}
                target="_blank"
                rel="noreferrer"
                data-testid="listen-live-btn"
                className="group inline-flex items-center gap-2 rounded-md bg-brand-amber px-6 py-3 font-semibold text-black amber-glow"
              >
                <Play className="h-4 w-4 fill-current" />
                Listen live
              </a>
            )}
            <button
              onClick={share}
              className="rounded-md border border-line bg-bg-elevated px-6 py-3 font-medium hover:bg-bg-surface transition inline-flex items-center gap-2"
              data-testid="share-hero-btn"
            >
              <Share2 className="h-4 w-4" />
              Share this show
            </button>
          </div>

          {(show.schedule || station.name) && (
            <div className="mt-10 grid sm:grid-cols-3 gap-3 max-w-2xl">
              {show.schedule && (
                <InfoCard icon={CalIcon} label="Airs" value={show.schedule} />
              )}
              <InfoCard icon={Radio} label="Station" value={station.name} />
              {(show.genre || station.genre) && (
                <InfoCard icon={Headphones} label="Genre" value={show.genre || station.genre} />
              )}
            </div>
          )}
        </div>
      </section>

      {/* DJ */}
      {dj && (
        <section className="border-b border-line bg-bg-surface">
          <div className="mx-auto max-w-5xl px-6 py-12 grid md:grid-cols-3 gap-8 items-start" data-testid="dj-section">
            <div className="md:col-span-1">
              {dj.avatar_url ? (
                <img src={dj.avatar_url} alt={dj.name} className="rounded-xl border border-line w-full max-w-[220px] aspect-square object-cover" />
              ) : (
                <div className="rounded-xl border border-line bg-bg-elevated w-full max-w-[220px] aspect-square flex items-center justify-center">
                  <span className="font-display text-6xl font-black text-brand-amber/60">{dj.name?.[0]?.toUpperCase()}</span>
                </div>
              )}
            </div>
            <div className="md:col-span-2">
              <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-brand-amber mb-2">// HOST</div>
              <h2 className="font-display text-3xl font-black tracking-tighter mb-1">{dj.name}</h2>
              {dj.real_name && dj.real_name !== dj.name && (
                <div className="text-sm text-ink-muted mb-3">aka {dj.real_name}</div>
              )}
              {dj.bio && <p className="text-sm text-ink-secondary leading-relaxed whitespace-pre-wrap">{dj.bio}</p>}
            </div>
          </div>
        </section>
      )}

      {/* Episodes */}
      {episodes?.length > 0 && (
        <section className="border-b border-line">
          <div className="mx-auto max-w-5xl px-6 py-12">
            <div className="flex items-end justify-between mb-6">
              <div>
                <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-brand-amber mb-2">// LATEST EPISODES</div>
                <h2 className="font-display text-3xl font-black tracking-tighter">Recent drops</h2>
              </div>
              <span className="font-mono text-xs text-ink-muted">{episodes.length} {episodes.length === 1 ? "episode" : "episodes"}</span>
            </div>
            <div className="space-y-3" data-testid="episodes-list">
              {episodes.map((ep, i) => (
                <article key={ep.episode_id} className="rounded-xl border border-line bg-bg-surface p-5 lift" data-testid={`public-ep-${ep.episode_id}`}>
                  <div className="flex items-start gap-4">
                    <div className="rounded-md bg-brand-amber/10 text-brand-amber p-3 shrink-0">
                      <Mic2 className="h-5 w-5" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-mono text-[10px] text-ink-muted">#{String(episodes.length - i).padStart(3, "0")}</span>
                        <span className="font-mono text-[10px] text-ink-muted">·</span>
                        <span className="font-mono text-[10px] text-ink-muted">{ep.duration_minutes}min</span>
                      </div>
                      <h3 className="font-display text-lg font-bold tracking-tight line-clamp-2">{ep.title}</h3>
                      {ep.summary && <p className="mt-2 text-sm text-ink-secondary line-clamp-2">{ep.summary}</p>}
                    </div>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Follow the station */}
      {socials.length > 0 && (
        <section className="border-b border-line bg-bg-surface">
          <div className="mx-auto max-w-5xl px-6 py-12 text-center">
            <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-brand-amber mb-2">// FOLLOW</div>
            <h2 className="font-display text-3xl font-black tracking-tighter mb-6">Tune in on every channel.</h2>
            <div className="flex flex-wrap justify-center gap-2">
              {socials.map((s) => (
                <a
                  key={s.key}
                  href={s.url}
                  target="_blank"
                  rel="noreferrer"
                  data-testid={`social-${s.key}`}
                  className="inline-flex items-center gap-2 rounded-full border border-line bg-bg-elevated px-4 py-2 text-sm hover:border-brand-amber/60 hover:text-cyan-300 transition"
                >
                  {s.label}
                  <ExternalLink className="h-3 w-3" />
                </a>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Engage CTA — request line, dedications & messages */}
      {station.station_id && (
        <section className="border-t border-line bg-bg-surface/60">
          <div className="mx-auto max-w-5xl px-6 py-10 text-center" data-testid="show-engage-band">
            <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-brand-amber mb-2">Get On Air</div>
            <h2 className="font-display text-2xl sm:text-3xl font-black tracking-tight mb-5">Be part of the show</h2>
            <div className="flex flex-wrap justify-center gap-3">
              <a href={`/engage/${station.station_id}?tab=request`} data-testid="show-engage-request"
                className="rounded-full bg-brand-amber px-5 py-2.5 text-sm font-bold text-black">Request a Song</a>
              <a href={`/engage/${station.station_id}?tab=dedication`} data-testid="show-engage-dedication"
                className="rounded-full border border-line px-5 py-2.5 text-sm font-bold text-ink-secondary hover:text-ink-primary">Send a Dedication</a>
              <a href={`/engage/${station.station_id}?tab=message`} data-testid="show-engage-message"
                className="rounded-full border border-line px-5 py-2.5 text-sm font-bold text-ink-secondary hover:text-ink-primary">Message the Station</a>
            </div>
          </div>
        </section>
      )}

      {/* Footer */}
      <footer className="py-12">
        <div className="mx-auto max-w-5xl px-6 text-center">
          <Link to="/" className="inline-flex items-center gap-2 text-xs text-ink-muted hover:text-cyan-300 transition font-mono uppercase tracking-wider" data-testid="footer-radioai">
            <Radio className="h-3.5 w-3.5" />
            Powered by BroadcastFlow AI <ArrowRight className="h-3 w-3" />
          </Link>
        </div>
      </footer>
    </div>
  );
}

function InfoCard({ icon: Icon, label, value }) {
  return (
    <div className="rounded-lg border border-line bg-bg-surface p-4">
      <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-wider text-ink-muted mb-1">
        <Icon className="h-3 w-3" />
        {label}
      </div>
      <div className="font-medium text-sm">{value}</div>
    </div>
  );
}
